import { createRouter, createWebHistory } from 'vue-router'

import store from '../store/index'
import { axiosInstance } from '@/services/api'
import ecosystemRoutes from './ecosystems'
import structuralDiversityRoutes from './structuralDiversities'
import irrigationTypes from './irrigationTypes'

import materialRoutes from './materials'
import elementUseRoutes from './elementUse'
import nurseryRoutes from './nurseries'
import plantingTypes from './plantingTypes'
import assemblyRoutes from './assemblies'
import transportationRoutes from './transportation'

const Admin = () => import('../views/admin/Admin.vue')
const Calculator = () => import('../views/calculator/Calculator.vue')
const CategoryEditor = () => import('../views/admin/categories/CategoryEditor')
const Dashboard = () => import('../views/admin/dashboard/Dashboard')

const Guide = () => import('../views/guide/Guide.vue')
const Landing = () => import('../views/landing/Landing.vue')
const MyProjects = () => import('../views/myProjects/MyProjects.vue')
const NewProject = () => import('../views/newProject/NewProject.vue')

const PasswordReset = () => import('../views/passwordReset/PasswordReset.vue')
const ProductTemplateForm = () => import('../views/admin/templates/ProductTemplateForm')
const Project = () => import('../views/project/Project.vue')
const ProjectLocation = () => import('../views/projectLocation/ProjectLocation.vue')
const Projects = () => import('../views/admin/projects/Projects')
const Scripts = () => import('../views/admin/scripts/Scripts')
const SignIn = () => import('../views/signIn/SignIn.vue')
const TemplateForm = () => import('../views/admin/templates/templateForm/TemplateForm')
const TemplateList = () => import('../views/admin/templates/TemplateList')
const Variables = () => import('../views/admin/variables/Variables')

const routes = [
  {
    path: '/',
    name: 'landing',
    component: Landing,
    meta: {
      requiresAuthStatus: null,
      page: {
        title: '',
        content: 'dark',
        toolbar: 'dark',
        showToolbar: false
      }
    }
  },
  {
    path: '/start',
    name: 'register',
    component: SignIn,
    meta: {
      requiresAuthStatus: false,
      page: {
        showToolbar: false,
        content: 'dark'
      }
    }
  },
  {
    path: '/reset_password',
    name: 'password_reset',
    component: PasswordReset,
    meta: {
      requiresAuthStatus: false,
      page: {
        showToolbar: false,
        content: 'dark'
      }
    }
  },
  {
    path: '/projects',
    name: 'projects',
    component: MyProjects,
    meta: {
      requiresAuthStatus: true,
      page: {
        showToolbar: false,
        title: 'My Projects',
        toolbar: 'dark',
        content: 'dark'
      }
    }
  },
  {
    path: '/projects/new',
    name: 'new-project',
    component: NewProject,
    meta: {
      requiresAuthStatus: true,
      page: {
        title: 'New Project',
        toolbar: 'dark',
        content: 'dark'
      }
    }
  },
  {
    path: '/projects/:id',
    name: 'project',
    component: Project,
    props: true,
    async beforeEnter (to) {
      const { id } = to.params
      try {
        const current = store.getters['projects/project']
        if (!current || current._id !== id) {
          await store.dispatch('projects/fetchProjectById', id)
        }
        const project = store.getters['projects/project']
        if (!project?.location || !project.location.area) {
          store.dispatch('showSnackbar', "Hey, looks like you haven't drawn a boundary for your project. Please draw one before continuing")
          return `/projects/${id}/location`
        }
      } catch (err) {
        return '/projects'
      }
    },
    meta: {
      requiresAuthStatus: true,
      page: {
        toolbar: 'white',
        content: 'white',
        transparentToolbar: true,
        showBackArrowOnMobile: true
      }
    }
  },
  {
    path: '/projects/:id/location',
    name: 'project-location',
    component: ProjectLocation,
    props: route => ({
      projectId: route.params.id
    }),
    meta: {
      requiresAuthStatus: true,
      page: {
        showToolbar: false
      }
    }
  },
  {
    path: '/guide',
    component: Guide,
    meta: {
      requiresAuthStatus: null,
      page: {
        showToolbar: false,
        content: 'dark'
      }
    }
  },
  {
    path: '/location',
    name: 'location',
    component: ProjectLocation,
    meta: {
      requiresAuthStatus: null,
      page: {
        showToolbar: false
      }
    }
  },
  {
    path: '/projects/:id/edit',
    name: 'calculator',
    component: Calculator,
    props: route => ({
      projectId: route.params.id,
      versionId: route.query.version
    }),
    meta: {
      requiresAuthStatus: true,
      page: {
        title: '',
        toolbar: 'dark',
        content: 'light'
      }
    }
  },
  {
    path: '/admin',
    component: Admin,
    meta: {
      requiresAuthStatus: 'admin',
      page: {
        toolbar: 'dark',
        content: 'light',
        title: 'Admin',
        backButtonPath: '/projects'
      }
    },
    children: [
      {
        path: '',
        name: 'admin',
        component: Dashboard,
        meta: {
          requiresAuthStatus: 'admin'
        }
      },
      {
        path: 'templates',
        name: 'templates',
        component: TemplateList,
        meta: { requiresAuthStatus: 'admin' }
      },
      {
        path: 'templates/new',
        name: 'newTemplate',
        component: TemplateForm,
        meta: { requiresAuthStatus: 'admin' }
      },
      {
        path: 'templates/:id',
        name: 'editTemplate',
        component: TemplateForm,
        props: true,
        meta: { requiresAuthStatus: 'admin' }
      },
      {
        path: 'templates/products/new',
        component: ProductTemplateForm,
        name: 'newProduct'
      },
      {
        path: 'templates/products/:id',
        component: ProductTemplateForm,
        name: 'editProduct',
        props: true
      },
      {
        path: 'categories',
        name: 'categories',
        component: CategoryEditor,
        meta: { requiresAuthStatus: 'admin' }
      },
      {
        path: 'variables',
        meta: {
          requiresAuthStatus: 'admin'
        },
        component: Variables
      },
      ...ecosystemRoutes,
      ...structuralDiversityRoutes,
      ...irrigationTypes,
      ...materialRoutes,
      ...elementUseRoutes,
      ...nurseryRoutes,
      ...plantingTypes,
      ...assemblyRoutes,
      ...transportationRoutes,
      {
        path: 'projects',
        meta: {
          requiresAuthStatus: 'admin'
        },
        component: Projects
      },
      {
        path: 'scripts',
        meta: {
          requiresAuthStatus: 'admin'
        },
        component: Scripts
      }
    ]
  }
]

const router = createRouter({
  history: createWebHistory('/'),
  routes
})

export default router

router.beforeEach(async (to, from, next) => {
  const requiresAuthStatus = to.meta.requiresAuthStatus

  if (requiresAuthStatus) {
    const { data: user } = await axiosInstance.get('/session')
    await store.dispatch('auth/setUserAuthenticated', user)
  }

  next()
})

router.afterEach((to, from) => {
  let { page } = to.meta
  if (!page) {
    page = to.matched[0] ? to.matched[0].meta.page : null
  }
  const pageDefaults = {
    showToolbar: true,
    editableTitleAction: null,
    toolbar: 'dark',
    content: 'light',
    title: '',
    backButtonPath: '',
    showUserOptions: true,
    showBackArrowOnMobile: false,
    transparentToolbar: false
  }
  if (page) {
    store.commit('setPage', {
      ...pageDefaults,
      ...page
    })
  }
})
